import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Globe, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';

const Auth = () => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { language, t } = useTranslation();

  useEffect(() => {
    // Check if user is already authenticated
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        // Force hard reload to prevent cached version
        const urlParams = new URLSearchParams(window.location.search);
        const redirect = urlParams.get('redirect');
        if (redirect) {
          window.location.href = decodeURIComponent(redirect);
        } else {
          window.location.href = '/';
        }
      }
    };
    checkUser();
  }, [navigate]);

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast({
        title: language === 'FR' ? 'Erreur' : 'Error',
        description: language === 'FR' ? 'Veuillez remplir tous les champs' : 'Please fill in all fields',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      if (isSignUp) {
        const redirectUrl = `${window.location.origin}/`;
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: redirectUrl,
            data: {
              full_name: email.split('@')[0] // Use email prefix as default name
            }
          }
        });
        
        if (error) {
          if (error.message.includes('already registered')) {
            toast({
              title: language === 'FR' ? 'Compte existant' : 'Account exists',
              description: language === 'FR' ? 'Ce compte existe déjà. Essayez de vous connecter.' : 'This account already exists. Try signing in.',
              variant: 'destructive',
            });
          } else {
            throw error;
          }
        } else {
          toast({
            title: language === 'FR' ? 'Inscription réussie' : 'Sign up successful',
            description: language === 'FR' ? 'Vérifiez votre email pour confirmer votre compte.' : 'Check your email to confirm your account.',
          });
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        
        if (error) {
          if (error.message.includes('Invalid login credentials')) {
            toast({
              title: language === 'FR' ? 'Erreur de connexion' : 'Login error',
              description: language === 'FR' ? 'Email ou mot de passe incorrect.' : 'Invalid email or password.',
              variant: 'destructive',
            });
          } else {
            throw error;
          }
        } else {
          toast({
            title: language === 'FR' ? 'Connexion réussie' : 'Sign in successful',
            description: language === 'FR' ? 'Bienvenue !' : 'Welcome!',
          });
          // Force hard reload to prevent cached version
          const urlParams = new URLSearchParams(window.location.search);
          const redirect = urlParams.get('redirect');
          if (redirect) {
            window.location.href = decodeURIComponent(redirect);
          } else {
            window.location.href = '/';
          }
        }
      }
    } catch (error: any) {
      toast({
        title: language === 'FR' ? 'Erreur' : 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setLoading(true);
    try {
      // Add cache busting parameter to force fresh load
      const redirectUrl = `${window.location.origin}/?v=${Date.now()}`;
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });
      
      if (error) throw error;
    } catch (error: any) {
      toast({
        title: language === 'FR' ? 'Erreur' : 'Error',
        description: error.message,
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-20" 
           style={{
             backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='7' cy='7' r='5'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
           }} />
      
      <div className="relative z-10 w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <img src="/logo.png" alt="DiskOver Logo" className="w-20 h-20 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">
            {language === 'FR' ? 'Bienvenue sur DiskOver' : 'Welcome to DiskOver'}
          </h1>
          <p className="text-white/80 mb-3">
            {language === 'FR' ? 'Promis vous n\'aurez aucun spam, ça nous aide à limiter les abus et garder une communauté quali!🙌' : 'We promise no spam, and it helps us limit abuse and keep a quality community!🙌'}
          </p>
        </div>

        <Card className="bg-white/10 border-white/20 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-white text-center">
              {isSignUp 
                ? (language === 'FR' ? 'Inscription' : 'Sign Up')
                : (language === 'FR' ? 'Connexion' : 'Sign In')
              }
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Google Auth */}
            <Button
              onClick={handleGoogleAuth}
              disabled={loading}
              variant="outline"
              className="w-full bg-white text-gray-900 hover:bg-gray-50 border-white/20"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {language === 'FR' ? 'Se connecter avec Google' : 'Sign in with Google'}
            </Button>

            <div className="relative">
              <Separator className="bg-white/20" />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white/10 px-2 text-white/80 text-sm">
                {language === 'FR' ? 'ou' : 'or'}
              </span>
            </div>

            {/* Email Auth Form */}
            <form onSubmit={handleEmailAuth} className="space-y-4">
              <div>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
                  <Input
                    type="email"
                    placeholder={language === 'FR' ? 'Adresse e-mail' : 'Email address'}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/60"
                    required
                  />
                </div>
              </div>

              <div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder={language === 'FR' ? 'Mot de passe' : 'Password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 bg-white/10 border-white/20 text-white placeholder:text-white/60"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white/80"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Remember me checkbox - only show on sign in */}
              {!isSignUp && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="remember-me"
                    checked={rememberMe}
                    onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                    className="border-white/40 data-[state=checked]:bg-white data-[state=checked]:text-primary"
                  />
                  <label
                    htmlFor="remember-me"
                    className="text-sm text-white/80 cursor-pointer"
                  >
                    {language === 'FR' ? 'Se souvenir de moi' : 'Remember me'}
                  </label>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-hero"
              >
                {loading 
                  ? (language === 'FR' ? 'Chargement...' : 'Loading...')
                  : isSignUp 
                    ? (language === 'FR' ? 'Créer un compte' : 'Create account')
                    : (language === 'FR' ? 'Se connecter' : 'Sign in')
                }
              </Button>
            </form>

            {/* Switch between sign in/up */}
            <div className="text-center">
              <button
                onClick={() => setIsSignUp(!isSignUp)}
                className="text-white/80 hover:text-white text-sm underline"
              >
                {isSignUp 
                  ? (language === 'FR' ? 'Déjà un compte ? Se connecter' : 'Already have an account? Sign in')
                  : (language === 'FR' ? 'Pas de compte ? S\'inscrire' : 'Don\'t have an account? Sign up')
                }
              </button>
            </div>

            {/* Back to app */}
            <div className="text-center pt-4">
              <button
                onClick={() => navigate('/')}
                className="text-white/60 hover:text-white/80 text-sm"
              >
                {language === 'FR' ? '← Retour à l\'application' : '← Back to app'}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Auth;